import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MobileLocation from "./MobileLocation";
import { COLOR, FONT, RADIUS, SHADOW, EASE, EASE_SPRING, GlobalMobileStyles, FIXED_HEADER_GRADIENT } from "./mobiletheme";
import shiperboxLogo from "../assets/shiperbox-logo.png";

export default function MobileHeader({
  searchValue, setSearchValue, onSearch,
  showLocation = false, showLogo = true, showTitleBar = false,
  title = "", activeTab = "home", isLoggedIn = true,
  onLoginClick, onHeightChange, showBackButton = false, onBack,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [transcript, setTranscript] = useState(t("header.listening"));
  const [isListening, setIsListening] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const headerRef = useRef(null);
  const gradientRef = useRef(null);
  const recognitionRef = useRef(null);
  const navTimeoutRef = useRef(null);
  const [gradientHeight, setGradientHeight] = useState(0);

  const TITLE_BAR_HEIGHT = 44;
  const TITLE_BAR_GAP = 14;
  
  const isHomeSearch = showLogo && showLocation;

  useEffect(() => {
    if (!gradientRef.current) return;
    const el = gradientRef.current;
    const report = () => {
      const h = el.offsetHeight;
      setGradientHeight(h);
      const reserved = showTitleBar ? h + TITLE_BAR_HEIGHT + TITLE_BAR_GAP : h;
      if (onHeightChange) onHeightChange(reserved);
    };
    report();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(report);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", report);
    return () => window.removeEventListener("resize", report);
  }, [onHeightChange, showLocation, showLogo, showTitleBar, title, showBackButton]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) try { recognitionRef.current.stop(); } catch (_) {}
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
    };
  }, []);

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return alert(t("header.voiceNotSupported"));

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = "en-IN";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    setShowVoiceModal(true);
    setTranscript(t("header.listening"));
    setIsListening(true);

    recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        else interimTranscript += event.results[i][0].transcript;
      }
      setTranscript(finalTranscript || interimTranscript);
      if (finalTranscript) {
        const cleanText = finalTranscript.replace(/\.$/, "").trim();
        setIsListening(false);
        if (setSearchValue) setSearchValue(cleanText);
        navTimeoutRef.current = setTimeout(() => {
          setShowVoiceModal(false);
          navigate(`/search?q=${encodeURIComponent(cleanText)}`);
        }, 600);
      }
    };

    recognition.onerror = (event) => {
      setTranscript(event.error === "no-speech" ? t("header.noSpeech") : t("header.micError"));
      setIsListening(false);
      navTimeoutRef.current = setTimeout(() => setShowVoiceModal(false), 2500);
    };

    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const closeVoiceModal = () => {
    if (recognitionRef.current) try { recognitionRef.current.stop(); } catch (_) {}
    setShowVoiceModal(false);
    setIsListening(false);
  };

  const SearchIcon = ({ size = 18, color = COLOR.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10.5" cy="10.5" r="7" />
      <line x1="20.5" y1="20.5" x2="15.8" y2="15.8" />
    </svg>
  );

  const MicIcon = ({ size = 19 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 7a4.5 4.5 0 0 1 9 0v4.5a4.5 4.5 0 0 1-9 0V7z" fill={COLOR.primary} />
      <circle cx="12" cy="5.5" r="1.2" fill={COLOR.ink} />
      <path d="M7.5 10.5l4.5 2 4.5-2" stroke={COLOR.onPrimary} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3M8 22h8" stroke={COLOR.ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const BigMicIcon = ({ size = 34 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8.5" y="2.5" width="7" height="12" rx="3.5" fill={COLOR.primary} />
      <circle cx="12" cy="6" r="1.2" fill={COLOR.ink} />
      <path d="M8.5 10.5l3.5 1.6 3.5-1.6" stroke={COLOR.onPrimary} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 12v0.5a6.5 6.5 0 0 0 13 0V12" stroke={COLOR.ink} strokeWidth="2.2" strokeLinecap="round" fill="none" />
    </svg>
  );

  const BackArrowIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLOR.ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="20" y1="12" x2="4" y2="12"></line>
      <polyline points="10 18 4 12 10 6"></polyline>
    </svg>
  );

  return (
    <>
      <GlobalMobileStyles />
      <style>{`
        .sb-titlebar-in { animation: sb-fadeSlide 0.28s ${EASE} both; }
        .sb-voice-modal-in { animation: sb-voiceUp 0.32s ${EASE_SPRING} both; }
        @keyframes sb-voiceUp { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .sb-back-btn:active { background: ${COLOR.primarySoft} !important; }
      `}</style>

      {showVoiceModal && (
        /*
         * Centering strategy: the overlay itself is a full-viewport flex
         * container that centers its child both ways. The modal has no
         * position/top/left/transform of its own, so it can never drift to
         * the bottom of the screen regardless of keyboard, safe-area insets,
         * or parent stacking context quirks.
         */
        <div style={styles.voiceOverlay} onClick={closeVoiceModal}>
          <div style={styles.voiceModal} className="sb-voice-modal-in" onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeModalBtn} className="sb-tap" onClick={closeVoiceModal}>✕</button>
            <h3 style={styles.voiceTitle}>{isListening ? t("header.speakNow") : t("header.processing")}</h3>
            <p style={{ ...styles.voiceTranscript, color: transcript === t("header.listening") ? COLOR.muted : COLOR.ink }}>{transcript}</p>
            <div style={{ ...styles.bigMicContainer, animation: isListening ? "sb-pulseGlow 1.5s infinite" : "none", background: isListening ? COLOR.gradientCTA : COLOR.line }}>
              <BigMicIcon size={34} />
            </div>
          </div>
        </div>
      )}

      <div ref={headerRef} style={styles.fixedHeader}>
        <div ref={gradientRef} style={styles.gradientWrap}>
            {(showLogo || showLocation) && (
              <div style={styles.headerTop}>
                {showLogo && (
                  <div style={styles.logoContainer} className="sb-tap" onClick={() => navigate("/")}>
                    <img src={shiperboxLogo} alt="Shiperbox" style={styles.logoImage} />
                  </div>
                )}
                {showLocation && (
                  <div style={styles.locationSlot}>
                    {isLoggedIn ? (
                      <MobileLocation />
                    ) : (
                      <div style={styles.loginWrapper}>
                        <button style={styles.loginBtn} className="sb-tap" onClick={onLoginClick}>{t("header.login")}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={isHomeSearch ? styles.searchRowHome : styles.searchRowOther}>
              <div
                style={{
                  ...styles.searchBox,
                  boxShadow: searchFocused
                    ? `0 0 0 4px ${COLOR.primarySoft}, 0 4px 12px rgba(0,0,0,0.06)`
                    : "0 2px 8px rgba(28,36,51,0.04)",
                  transform: searchFocused ? "translateY(-1px)" : "none",
                }}
              >
                <div style={styles.searchIconWrapper}><SearchIcon size={16} color={searchFocused ? COLOR.primaryDark : COLOR.muted} /></div>
                <input
                  type="search"
                  placeholder={t("header.searchPlaceholder")}
                  style={styles.searchInput}
                  value={searchValue}
                  onChange={(e) => setSearchValue && setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSearch && onSearch()}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                <button style={styles.micBtn} className="sb-tap" onClick={handleVoiceSearch} type="button">
                  <MicIcon size={19} />
                </button>
              </div>
            </div>
        </div>

        {showTitleBar && (
          <div style={{ ...styles.headerBottom, top: `${gradientHeight}px` }} className="sb-titlebar-in">
            {showBackButton && (
              <button style={styles.backBtn} className="sb-tap sb-back-btn" onClick={onBack}>
                <BackArrowIcon />
              </button>
            )}
            {title && <h3 style={styles.categoryTitle} className="sb-category-title">{title}</h3>}
          </div>
        )}
      </div>
    </>
  );
}

const styles = {
  fixedHeader: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, display: "flex", flexDirection: "column", background: "transparent", boxSizing: "border-box" },
  gradientWrap: { display: "flex", flexDirection: "column", background: `radial-gradient(120% 140% at 15% -20%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 55%), ${FIXED_HEADER_GRADIENT}`, paddingTop: "env(safe-area-inset-top, 0px)", boxSizing: "border-box", boxShadow: "0 4px 12px rgba(107,170,245,0.12)" },
  headerTop: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 12px 12px 0", flexWrap: "wrap" },
  logoContainer: { margin: "0 0 0 16px", display: "flex", alignItems: "center", cursor: "pointer", transition: `transform 0.18s ${EASE_SPRING}` },
  logoImage: { height: "32px", width: "auto", maxWidth: "120px", objectFit: "contain", display: "block" },
  locationSlot: { flex: 1, display: "flex", alignItems: "center", minWidth: 0 },
  loginWrapper: { display: "flex", justifyContent: "flex-end", width: "100%" },
  loginBtn: { background: COLOR.pop, color: COLOR.onPrimary, border: "none", padding: "10px 22px", borderRadius: "8px", fontWeight: "800", fontSize: "14px", cursor: "pointer", boxShadow: "0 4px 12px rgba(153, 246, 228, 0.4)", fontFamily: FONT.body },
  
  searchRowHome: { display: "flex", alignItems: "center", gap: "10px", padding: "2px 16px 16px", boxSizing: "border-box" },
  searchRowOther: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 16px 14px", boxSizing: "border-box", width: "100%" },
  
  searchBox: { flex: 1, height: "35px", display: "flex", alignItems: "center", backgroundColor: COLOR.paper, borderRadius: "10px", border: `1px solid ${COLOR.line}`, boxSizing: "border-box", transition: `box-shadow 0.25s ${EASE}, transform 0.25s ${EASE}` },
  searchIconWrapper: { paddingLeft: "14px", display: "flex", alignItems: "center" },
  searchInput: { flex: 1, minWidth: 0, border: "none", padding: "0 10px", outline: "none", fontSize: "14px", background: "transparent", color: COLOR.ink, fontFamily: FONT.body, fontWeight: "500" },
  micBtn: { width: "42px", height: "42px", border: "none", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, marginRight: "4px" },

  headerBottom: { position: "absolute", left: 0, right: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px", height: "44px", zIndex: 5 },
  categoryTitle: { margin: 0, background: "transparent", padding: "8px 18px", borderRadius: RADIUS.pill, boxShadow: "transparent", fontSize: "12px", fontWeight: "600", color: COLOR.ink, fontFamily: FONT.body, letterSpacing: "normal", textAlign: "center", boxSizing: "border-box" },
  backBtn: { position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", width: "28px", height: "28px", borderRadius: RADIUS.pill, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "transparent" },
  
  // Full-viewport flex overlay keeps the mic modal pinned to the exact
  // center of the screen — no fixed/top/left/transform math on the modal.
  voiceOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: COLOR.overlay, backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
    zIndex: 9999, animation: "sb-fadeSlide 0.2s ease",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "24px", boxSizing: "border-box",
  },
  voiceModal: {
    width: "85%", maxWidth: "340px", backgroundColor: COLOR.paper, borderRadius: RADIUS.xl,
    padding: "30px 20px", display: "flex", flexDirection: "column", alignItems: "center",
    position: "relative", boxShadow: "0 12px 40px rgba(15,23,42,0.18)",
  },
  closeModalBtn: { position: "absolute", top: "16px", right: "20px", background: COLOR.mist, border: "none", borderRadius: RADIUS.pill, width: "32px", height: "32px", fontSize: "16px", color: COLOR.muted, cursor: "pointer" },
  voiceTitle: { margin: "0 0 16px 0", fontSize: "20px", fontWeight: "700", color: COLOR.ink, fontFamily: FONT.display },
  voiceTranscript: { fontSize: "21px", textAlign: "center", minHeight: "60px", margin: "0 0 32px 0", fontStyle: "italic", maxWidth: "85%", wordBreak: "break-word" },
  bigMicContainer: { width: "72px", height: "72px", borderRadius: RADIUS.pill, display: "flex", alignItems: "center", justifyContent: "center", transition: "background-color 0.3s ease" },
};